import React, { useEffect, useState } from 'react';
import './LoginPage.css';
import atelierMonogram from './atelier-monogram.png';

// Set by App.js when the heartbeat detects that the session was revoked
// by a new login elsewhere. Read once on mount, cleared immediately so
// subsequent normal logins don't keep showing the banner.
const readSignedOutReason = () => {
  try {
    const raw = sessionStorage.getItem('signedOutReason');
    if (raw) sessionStorage.removeItem('signedOutReason');
    return raw || null;
  } catch { return null; }
};

const REASON_MESSAGES = {
  signed_in_elsewhere: 'You were signed out because your account signed in on another device or browser.',
  unknown: 'Your session has ended. Please sign in again.',
  revoked: 'Your session has ended. Please sign in again.',
};

const LoginPage = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [signedOutNotice, setSignedOutNotice] = useState(null);

  // 'signin' | 'forgot' — the forgot view swaps the form for a
  // request-a-reset-link email field.
  const [mode, setMode] = useState('signin');
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetNotice, setResetNotice] = useState(null);

  const showForgot = () => {
    setError(null);
    setSignedOutNotice(null);
    setResetNotice(null);
    // Prefill from whatever they typed in the username field (username or email).
    setResetEmail(username.trim());
    setMode('forgot');
  };

  const backToSignIn = () => {
    setResetNotice(null);
    setMode('signin');
  };

  const handleResetRequest = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setResetNotice(null);
    try {
      const response = await fetch('/api/chat/password-reset.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'request', identifier: resetEmail.trim() }),
      });
      const data = await response.json().catch(() => ({}));
      // The endpoint never reveals whether an account exists, so the success
      // copy is deliberately non-committal either way.
      setResetNotice({
        type: 'success',
        text: data.message || 'If that account exists, a reset link is on its way. Check your inbox.',
      });
    } catch (err) {
      setResetNotice({ type: 'error', text: 'Unable to reach the server. Please try again in a moment.' });
    } finally {
      setResetLoading(false);
    }
  };

  useEffect(() => {
    const reason = readSignedOutReason();
    if (reason) setSignedOutNotice(REASON_MESSAGES[reason] || REASON_MESSAGES.revoked);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Call the authentication API
      // Post to /api/authenticate.php — the canonical backend path.
      // dev-style tenants route /api/... straight to backend through ingress,
      // so they need the literal .php in the URL. demo-style tenants route
      // /api/authenticate through the frontend nginx pod; the configmap has
      // an exact-match rule (location = /api/authenticate.php) that forwards
      // .php paths through without doubling the suffix.
      const response = await fetch('/api/authenticate.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store user info + single-session token in sessionStorage.
        // The token is what App.js heartbeats against; if the backend
        // has revoked it (a login elsewhere), we're kicked out.
        sessionStorage.setItem('user', JSON.stringify(data.user));
        if (data.session_token) {
          sessionStorage.setItem('sessionToken', data.session_token);
        } else {
          sessionStorage.removeItem('sessionToken');
        }
        setSignedOutNotice(null);
        onLoginSuccess(data.user);
      } else {
        setError(data.message || 'Invalid username or password');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Unable to connect to authentication service');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-background">
        <div className="bg-overlay"></div>
      </div>

      <div className="login-container">
        <div className="login-box">
          <div className="login-header">
            <div className="login-logo">
              <img src={atelierMonogram} alt="Atelier" className="login-logo-img" />
            </div>
            <div className="login-wordmark">ATELIER</div>
            <div className="login-eyebrow"><span>Studio Systems</span></div>
            <h1 className="login-title">Interior Design CRM</h1>
            <p className="login-subtitle">Transform Spaces, Elevate Experiences</p>
          </div>

          {mode === 'signin' ? (
            <form onSubmit={handleSubmit} className="login-form">
              {signedOutNotice && !error && (
                <div className="notice-message">
                  <span className="notice-icon">🔒</span>
                  {signedOutNotice}
                </div>
              )}
              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  {error}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="username" className="form-label">
                  Username
                </label>
                <input
                  id="username"
                  type="text"
                  className="form-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                className="login-button"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Signing In...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>

              <button
                type="button"
                className="login-link-button"
                onClick={showForgot}
                disabled={loading}
              >
                Forgot your password?
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetRequest} className="login-form">
              <p className="login-reset-intro">
                Enter your username (or the email on your account) and we'll send a link to reset your password.
              </p>
              {resetNotice && (
                <div className={resetNotice.type === 'error' ? 'error-message' : 'notice-message'}>
                  <span className={resetNotice.type === 'error' ? 'error-icon' : 'notice-icon'}>
                    {resetNotice.type === 'error' ? '⚠️' : '✉️'}
                  </span>
                  {resetNotice.text}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="resetEmail" className="form-label">
                  Username or email
                </label>
                <input
                  id="resetEmail"
                  type="text"
                  className="form-input"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="Enter your username or email"
                  required
                  disabled={resetLoading}
                />
              </div>

              <button
                type="submit"
                className="login-button"
                disabled={resetLoading}
              >
                {resetLoading ? (
                  <>
                    <span className="spinner"></span>
                    Sending...
                  </>
                ) : (
                  'Send reset link'
                )}
              </button>

              <button
                type="button"
                className="login-link-button"
                onClick={backToSignIn}
                disabled={resetLoading}
              >
                ← Back to sign in
              </button>
            </form>
          )}

          <div className="login-ornament" aria-hidden="true">
            <svg viewBox="0 0 160 14" xmlns="http://www.w3.org/2000/svg">
              <line x1="6" y1="7" x2="66" y2="7" stroke="#b7ab93" strokeWidth="1" />
              <g transform="translate(74 0)">
                <path d="M6 7 C 6 3, 9 1, 13 1 C 12 6, 9 8, 6 7 Z" fill="#9aa06a" />
                <path d="M6 7 C 6 11, 9 13, 13 13 C 12 8, 9 6, 6 7 Z" fill="#9aa06a" opacity="0.75" />
                <line x1="0" y1="7" x2="6" y2="7" stroke="#9aa06a" strokeWidth="1" />
              </g>
              <line x1="94" y1="7" x2="154" y2="7" stroke="#b7ab93" strokeWidth="1" />
            </svg>
          </div>
          <div className="login-footer">
            <p className="login-footer-text">
              Powered by SuiteCRM 7.14.7 · API v8 Enabled
            </p>
          </div>
        </div>

        <div className="login-features">
          <div className="feature-item">
            <div className="feature-item-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 11.5 12 5l8 6.5" />
                <path d="M6 10.5V19h12v-8.5" />
                <path d="M10 19v-4h4v4" />
              </svg>
            </div>
            <div className="feature-item-text">
              <h3>Project Management</h3>
              <p>Track design projects from concept to completion</p>
            </div>
          </div>
          <div className="feature-item">
            <div className="feature-item-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="2.4" />
                <path d="M8 18c0-2.5 1.8-4 4-4s4 1.5 4 4" />
                <circle cx="5.3" cy="9.6" r="1.7" />
                <path d="M2.5 17.3c0-2 1.2-3.2 3-3.3" />
                <circle cx="18.7" cy="9.6" r="1.7" />
                <path d="M21.5 17.3c0-2-1.2-3.2-3-3.3" />
              </svg>
            </div>
            <div className="feature-item-text">
              <h3>Client Relations</h3>
              <p>Build lasting relationships with your clients</p>
            </div>
          </div>
          <div className="feature-item">
            <div className="feature-item-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 20h16" />
                <rect x="5" y="12" width="3" height="6" />
                <rect x="10.5" y="8" width="3" height="10" />
                <rect x="16" y="4" width="3" height="14" />
              </svg>
            </div>
            <div className="feature-item-text">
              <h3>Analytics &amp; Insights</h3>
              <p>Make data-driven design decisions</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
